import { useEffect, useRef, useState } from 'react';
import { Alert, Button, FormControl, FormLabel, Input, Stack, Typography } from '@mui/joy';
import ConsentCheckbox from './ConsentCheckbox';
import { apiDoctorConfirmationRequest, apiDoctorConfirmationVerify, setTokens } from '../../auth';

export default function DoctorLoginConfirmation({ challenge, masked, consentAccepted,
  onConsentChange, onSuccess, onCancel }) {
  const [delivery, setDelivery] = useState(null);
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [waitUntil, setWaitUntil] = useState(0);
  const [now, setNow] = useState(Date.now());
  const [expired, setExpired] = useState(false);
  const epoch = useRef(0);
  const pending = useRef(false);
  useEffect(() => {
    ++epoch.current;
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => { ++epoch.current; clearInterval(timer); };
  }, [challenge]);
  const wait = Math.max(0, Math.ceil((waitUntil - now) / 1000));
  const execute = async (operation) => {
    if (pending.current || !consentAccepted || expired) return;
    const generation = epoch.current;
    pending.current = true; setBusy(true); setError('');
    try {
      const data = await operation();
      if (generation !== epoch.current) return;
      if (!data?.success) throw new Error(data?.error || 'Не удалось подтвердить вход');
      return data;
    } catch (e) {
      if (generation !== epoch.current) return;
      const status = e?.response?.status;
      if (status === 410) setExpired(true);
      const retry = Number(e?.response?.data?.retry_after);
      if (status === 429) setWaitUntil(Date.now() + (Number.isFinite(retry) && retry > 0 ? retry : 60) * 1000);
      setError(e?.response?.data?.error || e.message || 'Ошибка сети. Попробуйте ещё раз');
    } finally {
      if (generation === epoch.current) { pending.current = false; setBusy(false); }
    }
  };
  const send = async (channel) => {
    if (wait > 0) return;
    const generation = epoch.current;
    const data = await execute(() => apiDoctorConfirmationRequest({ challenge, channel, consentAccepted }));
    if (!data || generation !== epoch.current) return;
    setDelivery(data); setCode('');
    setWaitUntil(Date.now() + Number(data.retry_after || 60) * 1000);
  };
  const verify = async () => {
    if (!delivery || code.length !== delivery.code_length) return;
    const generation = epoch.current;
    const data = await execute(() => apiDoctorConfirmationVerify({ challenge, code, consentAccepted }));
    if (!data || generation !== epoch.current) return;
    if (!data.access || !data.refresh || !data.profile?.id) {
      setError('Сервер не подтвердил вход. Начните заново.'); return;
    }
    setTokens({ access: data.access, refresh: data.refresh });
    onSuccess(data.profile);
  };
  return (
    <Stack spacing={1.5}>
      <Typography level="title-md">Подтвердите вход</Typography>
      <Typography level="body-sm">Код придёт на телефон врача: {delivery?.destination_masked || masked}.</Typography>
      {error && <Alert color="danger">{error}</Alert>}
      {!delivery && <Button loading={busy} disabled={!consentAccepted || expired || wait > 0}
        onClick={() => send('call')}>Получить код звонком{wait > 0 ? ` (${wait})` : ''}</Button>}
      {delivery && <>
        <Typography level="body-sm">{delivery.channel === 'call'
          ? `Введите последние ${delivery.code_length} цифр номера входящего звонка`
          : `Введите ${delivery.code_length} цифр из SMS`}</Typography>
        <FormControl><FormLabel>Код подтверждения</FormLabel>
          <Input autoFocus value={code} disabled={busy || expired}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, delivery.code_length))}
            onKeyDown={(e) => { if (e.key === 'Enter') verify(); }}
            slotProps={{ input: { inputMode: 'numeric', autoComplete: 'one-time-code', maxLength: delivery.code_length } }} />
        </FormControl>
        <Button loading={busy} disabled={!consentAccepted || expired || code.length !== delivery.code_length}
          onClick={verify}>Подтвердить и войти</Button>
      </>}
      <ConsentCheckbox checked={consentAccepted} onChange={onConsentChange} disabled={busy} />
      <Stack direction="row" spacing={1}>
        {delivery && <Button variant="plain" size="sm" disabled={busy || !consentAccepted || expired || wait > 0}
          onClick={() => send(delivery.channel)}>Повторить{wait > 0 ? ` (${wait})` : ''}</Button>}
        <Button variant="plain" size="sm" disabled={busy || !consentAccepted || expired || wait > 0}
          onClick={() => send('sms')}>Получить SMS{!delivery && wait > 0 ? ` (${wait})` : ''}</Button>
        <Button variant="plain" color="neutral" size="sm" disabled={busy} onClick={onCancel}>Назад</Button>
      </Stack>
    </Stack>
  );
}